import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { OtpDetails } from 'ehs-react';
import {
  Breakpoints,
  intl,
  NotificationType,
  notify,
  OtpConfirmationControl,
  QRCode,
  showError,
  Tool,
  useBreakpoint
} from 'ehs-react';
import { usePortletStore } from '../App';
import { sendTwoFactorPassword } from '../stores/ajax';
import { useLoginCredentialsStore } from '../stores/loginCredentials.store';
import { getUniqueOtpDetails } from '../util/login-response-helper';
import { PortletStoreApi } from '..';

export type OtpVerificationAreaParameters = {
  onSubmit: (
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void
  ) => void;
};

export function OtpVerificationArea({
  onSubmit
}: Readonly<OtpVerificationAreaParameters>): React.JSX.Element {
  const [nrOfOtpAttempts, lengthOfCode] = usePortletStore(state => [
    state.nrOfOtpAttempts,
    state.configuration.lengthOfOtp
  ]);
  const [
    viewState,
    otpSendDetails,
    userName,
    password,
    assertionId,
    secondFactorMethod,
    qrCode,
    appSecretKey,
    otpAuthUri,
    pageLoading
  ] = useLoginCredentialsStore(state => [
    state.viewState,
    state.otpSendDetails,
    state.userName,
    state.password,
    state.assertionId,
    state.selectedTwoFactorMethod?.sendMethod,
    state.qrCode,
    state.appSecretKey,
    state.otpAuthUri,
    state.pageLoading
  ]);
  const isMobile = useBreakpoint(Breakpoints.MOBILE);

  const sendAuthenticationCodeCallHandler = useCallback(
    (
      onSendingSuccess: (param: OtpDetails) => void,
      onSendingError: (msg?: string, resendDisabled?: boolean) => void
    ) => {
      sendTwoFactorPassword({
        url: PortletStoreApi.getState().loginServletUrl,
        user: userName,
        password: password,
        assertionId: assertionId,
        twofa: secondFactorMethod
      })
        .then(p => {
          const otpDetails = getUniqueOtpDetails(p.data);
          if (otpDetails) {
            onSendingSuccess(otpDetails);
          }
        })
        .catch(e => {
          showError(e);
          onSendingError();
        });
    },
    [assertionId, password, userName, secondFactorMethod]
  );
  const validateAuthenticationCodeCallHandler = useCallback(
    (
      authenticationCode: string,
      onCodeMatched: () => void,
      onCodeMismatched: (msg?: string) => void
    ) =>
      setTimeout(() => {
        onSubmit(authenticationCode, onCodeMatched, onCodeMismatched);
      }, 0),
    [onSubmit]
  );

  const otpControlRef = useRef<{ reset: () => void }>(null);
  useEffect(() => {
    if (nrOfOtpAttempts > 0) {
      otpControlRef.current?.reset();
    }
  }, [nrOfOtpAttempts]);

  const url = PortletStoreApi.getState().url;
  const isPatientPortal = useMemo(() => url.includes('patient-portal'), [url]);
  const copyLink = useCallback(() => {
    if (appSecretKey) {
      navigator.clipboard.writeText(appSecretKey);
      notify({
        type: NotificationType.SUCCESS,
        title: intl(
          'twofactorconfigportlet.view.config.totp.secret.copy.successtoast'
        )
      });
    }
  }, [appSecretKey]);

  if (!otpSendDetails || viewState !== 'TWO_FACTOR_ENTERING') {
    return <></>;
  }
  return (
    <div className="otp-verification-area progress-card-react">
      {isPatientPortal && !qrCode && (
        <div className="authentication-content-image-container">
          <img
            src="/o/LoginHook/images/authentication_wizard_otp.svg"
            role="none"
          />
        </div>
      )}
      <h2 className="font-22 sh-font">
        {intl(
          'patientSelfOnboardingPortlet.authenticationWizard.stepConfirmation'
        )}
      </h2>
      {qrCode && (
        <div className="totp-setup-area">
          <div className="qr-code-wrapper">
            <QRCode
              qrcodeSVG={qrCode}
              width={200}
              height={200}
              ariaLabelledby="qr-code-description"
            />
          </div>
          <div className="qr-code-description-area font-16">
            <p id="qr-code-description">
              {intl('twofactorconfigportlet.view.setup.app.intro')}
            </p>
            <div className="totp-secret-container">
              <b>{appSecretKey}</b>
              <Tool
                icon="copy-filled"
                tooltip={intl(
                  'twofactorconfigportlet.view.config.totp.secret.copy.tooltip'
                )}
                text=""
                className="copy-icon"
                onClick={copyLink}
                data-testid="copy-secret-action"
              />
            </div>
          </div>
          {isMobile && otpAuthUri && (
            <button
              className="launch-auth-app font-16"
              onClick={() => (window.location.href = otpAuthUri)}
            >
              {intl('twofactorconfigportlet.view.setup.app.launcher')}
            </button>
          )}
        </div>
      )}
      <OtpConfirmationControl
        ref={otpControlRef}
        autoFocusFirstInput={true}
        onOtpValid={() => {}}
        lengthOfCode={lengthOfCode}
        validateAuthenticationCodeCall={validateAuthenticationCodeCallHandler}
        sendAuthenticationCodeCall={sendAuthenticationCodeCallHandler}
        initialOtpDetails={otpSendDetails}
        disabled={pageLoading}
      />
    </div>
  );
}
